import { questionSetRollupKey } from 'js/utils/org-assessment-rollup';

import { orderAssessmentQuestionSets } from 'js/utils/org-assessment-order';

function columnsFromQuestionSets(questionSets, hasMultipleQuestionSets) {
  if (!hasMultipleQuestionSets) {
    return questionSets.flatMap((qs) =>
      (qs.questions ?? []).map(({ key, text }) => ({
        combinedKey: `${qs.key}|${key}`,
        columnType: 'question',
        questionSetKey: qs.key,
        questionKey: key,
        text: text ?? key,
      }))
    );
  }

  return questionSets.flatMap((qs) => {
    const questionKeys = (qs.questions ?? []).map((q) => q.key);

    return [
      {
        combinedKey: questionSetRollupKey(qs.key),
        columnType: 'questionSet',
        questionSetKey: qs.key,
        questionKeys,
        text: qs.name ?? qs.key,
      },
      ...(qs.questions ?? []).map(({ key, text }) => ({
        combinedKey: `${qs.key}|${key}`,
        columnType: 'question',
        questionSetKey: qs.key,
        questionKey: key,
        text: text ?? key,
      })),
    ];
  });
}

function columnDataFromScores(questionScores) {
  const data = [];
  const seen = new Set();

  for (const row of questionScores ?? []) {
    for (const s of row.scores ?? []) {
      const combinedKey = `${s.question_set_key}|${s.question_key}`;
      if (seen.has(combinedKey)) continue;
      seen.add(combinedKey);
      data.push({
        combinedKey,
        questionKey: s.question_key,
        questionSetKey: s.question_set_key,
        text: s.question_text ?? s.question_key,
      });
    }
  }

  return data;
}

function findQuestionSetColumn(columns, questionSetKey) {
  return columns.find(
    (column) =>
      column.columnType === 'questionSet' &&
      column.questionSetKey === questionSetKey
  );
}

function insertIndexAfterQuestionSet(columns, questionSetKey) {
  for (let i = columns.length - 1; i >= 0; i -= 1) {
    if (columns[i].questionSetKey === questionSetKey) {
      return i + 1;
    }
  }

  return columns.length;
}

function appendScoreOnlyColumn(columns, row, hasMultipleQuestionSets, seen) {
  const { combinedKey, questionSetKey, questionKey, text } = row;

  if (seen.has(combinedKey)) {
    return;
  }

  if (!hasMultipleQuestionSets) {
    columns.push({
      combinedKey,
      columnType: 'question',
      questionSetKey,
      questionKey,
      text,
    });
    seen.add(combinedKey);
    return;
  }

  const rollupKey = questionSetRollupKey(questionSetKey);

  if (!seen.has(rollupKey)) {
    const insertAt = insertIndexAfterQuestionSet(columns, questionSetKey);
    columns.splice(insertAt, 0, {
      combinedKey: rollupKey,
      columnType: 'questionSet',
      questionSetKey,
      questionKeys: [],
      text: questionSetKey,
    });
    seen.add(rollupKey);
  }

  const questionSetColumn = findQuestionSetColumn(columns, questionSetKey);

  if (
    questionSetColumn &&
    !questionSetColumn.questionKeys.includes(questionKey)
  ) {
    questionSetColumn.questionKeys.push(questionKey);
  }

  const insertAt = insertIndexAfterQuestionSet(columns, questionSetKey);
  columns.splice(insertAt, 0, {
    combinedKey,
    columnType: 'question',
    questionSetKey,
    questionKey,
    text,
  });
  seen.add(combinedKey);
}

export function buildScorecardColumnData(
  assessment,
  questionScores,
  assessmentTemplate = null
) {
  const questionSets = orderAssessmentQuestionSets(
    assessment?.question_sets ?? [],
    assessmentTemplate
  );
  const hasMultipleQuestionSets = questionSets.length > 1;
  const columns = columnsFromQuestionSets(
    questionSets,
    hasMultipleQuestionSets
  );
  const seen = new Set(columns.map((column) => column.combinedKey));

  for (const row of columnDataFromScores(questionScores)) {
    appendScoreOnlyColumn(columns, row, hasMultipleQuestionSets, seen);
  }

  return columns;
}

export function questionSetRollupsFromColumnData(columnData) {
  return columnData
    .filter((column) => column.columnType === 'questionSet')
    .map(({ questionSetKey, questionKeys }) => ({
      questionSetKey,
      questionKeys,
    }));
}
